<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useRoute } from 'vue-router'
import { useVModel } from '@vueuse/core'
import DxTreeView from 'devextreme-vue/tree-view'

import { useDetectDevice, useOrientation, useTouch } from '@/composables/device'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import router, { menuRoutes, menuRouteEnum } from '@/router'

import AvatarEdit from '@/components/shared/AvatarEdit.vue'
import LanguageSelector from '@/components/shared/LanguageSelector.vue'
import FontSizeSelector from '@/components/shared/FontSizeSelector.vue'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()

const { isTouchDevice } = useTouch()
const { isLandscape } = useOrientation()
const { isMobile } = useDetectDevice()

const props = defineProps({
  drawer: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:drawer'])
const drawerVisible = useVModel(props, 'drawer', emit)

const companyAddress = computed(() => {
  const cd = accountingStore.companyData
  if (!cd) return ''
  const parts = [
    [cd.street, cd.street_number].filter(Boolean).join(' '),
    [cd.zip_code, cd.city].filter(Boolean).join(' '),
  ].filter(Boolean)
  return parts.join(', ')
})

const overlay = ref(webSiteStore.sideBarPinned)
const treeView = ref(null)
const menuEntries = webSiteStore.getMenuEntriesWithRoute().filter(entry => isMobile.value ? entry.visible_mobile : entry.visible)
const expandedIds = ref(new Set())
const isExpanded = (id) => expandedIds.value.has(id)
const isInside = ref(false)
const route = useRoute()

const onMouseEnter = () => {
  if (!isTouchDevice) {
    isInside.value = true
    setTimeout(() => {
      if (isInside.value) {
        setTimeout(() => {
          webSiteStore.sideBarExpanded = true
        }, 200)
        overlay.value = false
      }
    }, 500)
  }
}

const onMouseLeave = () => {
  if (!isTouchDevice) {
    isInside.value = false
    setTimeout(() => {
      if (!isInside.value) {
        setTimeout(() => {
          webSiteStore.sideBarExpanded = false
        }, 200)
        overlay.value = webSiteStore.sideBarPinned
        if (treeView.value) {
          treeView.value.collapseAll()
        }
      }
    }, 500)
  }
}

const onSideBarPinClick = () => {
  webSiteStore.sideBarPinned = !webSiteStore.sideBarPinned
  overlay.value = webSiteStore.sideBarPinned
}

const saveTreeViewInstance = (e) => {
  treeView.value = e.component
}

const onItemClick = (e) => {
  if (!isLeaf(e.itemData)) {
    if (expandedIds.value.has(e.itemData.id)) {
      const next = new Set(expandedIds.value)
      next.delete(e.itemData.id)
      expandedIds.value = next
    } else {
      expandedIds.value = new Set([...expandedIds.value, e.itemData.id])
    }
  }
  for (const item of menuEntries) {
    if (item.parent_id === e.itemData.parent_id && item.id !== e.itemData.id) {
      treeView.value.collapseItem(item)
      const next = new Set(expandedIds.value)
      next.delete(item.id)
      expandedIds.value = next
    }
  }
}

const onItemExpanded = (e) => {
  if (e.itemData.parent_id) {
    e.itemElement.classList.remove('dx-state-focused')
    e.itemElement.classList.add('dx-theme-accent-as-text-color')
  }
}

const onItemCollapsed = (e) => {
  e.itemElement.classList.remove('dx-theme-accent-as-text-color')
}

const userName = computed(() => {
  if (webSiteStore.user && webSiteStore.user.firstName) {
    return `${webSiteStore.user.firstName} ${webSiteStore.user.lastName}`
  }
  return webSiteStore.user?.username ?? ''
})

const itemDepth = (item) => {
  let depth = 0
  let current = item
  while (current?.parent_id) {
    depth += 1
    current = findItemById(current.parent_id)
  }
  return depth
}

const indentClass = (item) => {
  switch (itemDepth(item)) {
    case 1: return 'q-ml-md'
    case 2: return 'q-ml-xl'
    default: return 'q-ml-sm'
  }
}

const nodeClass = (item) => {
  const stateClass = selectedMenuEntryIds.value.includes(item.id) ? 'selected menu-entry--active' : 'grayed-out'
  return [stateClass, indentClass(item)]
}

const findItemById = (id) => {
  for (const item of menuEntries) {
    if (item.id === id) {
      return item
    }
  }
  return null
}

const isCompanySelected = computed(() => route.meta.reference === menuRouteEnum.COMPANY)

const currentMenuEntry = computed(() => {
  if (isCompanySelected.value) return null
  // Exact match first
  let entry = menuEntries.find(item => item.route === route.path)
  // If no exact match, find the longest matching route
  if (!entry) {
    const matches = menuEntries.filter(item => item.route && route.path.startsWith(item.route))
    if (matches.length) {
      entry = matches.reduce((longest, current) => 
        current.route.length > longest.route.length ? current : longest
      )
    }
  }
  return entry
})

const selectedMenuEntryIds = computed(() => {
  let entry = currentMenuEntry.value
  const ids = [entry?.id]
  while (entry?.parent_id) {
    ids.push(entry.parent_id)
    entry = findItemById(entry.parent_id)
  }
  return ids
})

const onSearchClick = () => {
  webSiteStore.showSearch = true
  if (isMobile.value) drawerVisible.value = false
}

const onLogoutClick = async() => (await webSiteStore.logoutAsync())
const navigateToCompany = () => { router.push(menuRoutes[menuRouteEnum.COMPANY]) }
const closeDrawerOnMobile = () => { if (isMobile.value) drawerVisible.value = false }
const onMyProfileClick = async() => {
  closeDrawerOnMobile()
  await webSiteStore.navigateToMyProfile()
}

const isLeaf = (item) => !menuEntries.some(entry => entry.parent_id === item.id)

const openInNewTab = (item) => {
  if (item.route) {
    const resolved = router.resolve(item.route)
    window.open(resolved.href, '_blank')
  }
}

const onDetailClick = async (item) => {
  if (item.route) {
    await router.push(item.route)
    if (isMobile.value && !isLandscape.value && item.id === currentMenuEntry.value?.id) {
      drawerVisible.value = false
    }
  }
}

</script>
<template>
  <div>
    <q-drawer
      v-model="drawerVisible"
      class="nav"
      bordered
      elevated
      no-swipe-open
      no-swipe-close
      no-swipe-backdrop
      touchless
      :mini="overlay"
      :width="250"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <q-list
        class="nav-tree"
        :class="{ 'nav-tree--mobile': isMobile }"
      >
        <div class="nav-header">
          <q-item
            v-if="drawer && !isLandscape"
            v-ripple
            class="menu-item menu-entry nav-header__user"
            clickable
            @click="onMyProfileClick"
          >
            <q-item-section avatar>
              <avatar-edit size="2rem" :label="i18n.t('common.myProfile')" @click="onMyProfileClick" />
            </q-item-section>
            <q-item-section>{{ userName }}</q-item-section>
          </q-item>
          <q-item
            v-ripple
            class="menu-item menu-entry nav-header__company"
            :class="isCompanySelected ? 'selected menu-entry--active' : 'grayed-out'"
            clickable
            @click="navigateToCompany"
          >
            <q-item-section avatar>
              <avatar-edit
                :image="accountingStore.getCompanyLogoUrl()"
                :initials="accountingStore.getCompanyInitials()"
                :label="i18n.t('common.editCompany')"
                size="2.5rem"
                @click="navigateToCompany"
              />
            </q-item-section>
            <q-item-section style="flex-flow: column;">
              <q-item-label class="company-name">
                {{ accountingStore.companyData?.name }}
              </q-item-label>
              <q-item-label
                v-if="companyAddress"
                caption
                class="company-address"
              >
                {{ companyAddress }}
              </q-item-label>
            </q-item-section>
            <q-item-section v-if="isMobile" side>
              <div class="nav-header__actions">
                <q-btn
                  flat
                  round
                  dense
                  icon="mdi-magnify"
                  @click.stop="onSearchClick"
                />
              </div>
            </q-item-section>
          </q-item>
        </div>
        <dx-tree-view
          key-expr="id"
          data-structure="plain"
          parent-id-expr="parent_id"
          expand-event="click"
          collapse-icon="none"

          :data-source="menuEntries"
          @initialized="saveTreeViewInstance"
          @item-click="onItemClick"
          @item-expanded="onItemExpanded"
          @item-collapsed="onItemCollapsed"
        >
          <template #item="data">
            <q-item
              v-ripple
              class="menu-item menu-entry"
              clickable
              :class="[nodeClass(data.data)]"
              @click="onDetailClick(data.data)"
            >
              <q-item-section
                avatar
              >
                <q-icon
                  :name="data.data.icon"
                />
              </q-item-section>

              <q-item-section>
                {{ i18n.t(data.data.code) }}
              </q-item-section>
              <q-item-section
                v-if="!isMobile && data.data.route && isLeaf(data.data)"
                side
              >
                <q-icon
                  name="mdi-open-in-new"
                  class="open-in-new"
                  color="primary"
                  size="xs"
                  @click.stop="openInNewTab(data.data)"
                />
              </q-item-section>
              <q-item-section
                v-if="!isLeaf(data.data)"
                side
              >
                <q-icon
                  name="mdi-chevron-right"
                  :class="['expand-chevron', { 'expand-chevron--open': isExpanded(data.data.id) }]"
                  size="xs"
                />
              </q-item-section>
            </q-item>
          </template>
        </dx-tree-view>
        <q-separator v-if="isMobile && !isLandscape" />
        <q-list v-if="isMobile && !isLandscape">
          <q-item class="q-ml-sm">
            <language-selector width="100%" />
          </q-item>
          <q-item
            class="align-center"
          >
            <font-size-selector />
          </q-item>
        </q-list>
      </q-list>
      <q-list class="nav-footer z-top q-pa-sm">
        <q-item
          v-if="drawer && isLandscape"
          v-ripple
          clickable
          @click="onSideBarPinClick"
        >
          <q-item-section avatar>
            <q-icon :name="webSiteStore.sideBarPinned ? 'mdi-pin' : 'mdi-pin-off'" />
          </q-item-section>

          <q-item-section>
            <span v-if="webSiteStore.sideBarPinned">{{ i18n.t('common.pin') }}</span>
            <span v-else>{{ i18n.t('common.unPin') }}</span>
          </q-item-section>
        </q-item>

        <div
          v-if="drawer && !isLandscape"
          class="column items-center q-px-sm q-pb-sm"
        >
          <q-btn
            v-ripple
            color="primary"
            label="Logout"
            class="large-btn"
            @click="onLogoutClick"
          />
          <q-badge
            style="background: transparent; color: grey;"
            :label="`app v.${portalStore.version}`"
            class="q-mt-sm"
          />
        </div>
      </q-list>
    </q-drawer>
  </div>
</template>
<style lang="scss">
.nav {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.nav-tree {
  display: flex; flex-direction: column; flex: 1; min-height: 0;
}
.nav-footer {
  flex: none;
}
.nav-tree > .menu-item {
  margin-left: 0.5rem;
}
.nav .dx-treeview {
  flex-grow: 1;
  padding-top: 0.75rem;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba($primary, 0.28) transparent;
}
.nav .dx-treeview::-webkit-scrollbar {
  width: 0.4rem;
}
.nav .dx-treeview::-webkit-scrollbar-thumb {
  background-color: rgba($primary, 0.28);
  border-radius: 0.5rem;
}
.nav .dx-treeview::-webkit-scrollbar-track {
  background: transparent;
}
.nav-tree--mobile .dx-treeview {
  scrollbar-width: none;
}
.nav-tree--mobile .dx-treeview::-webkit-scrollbar {
  display: none;
}
.nav .menu-item .q-item__section--main {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.nav .dx-treeview-node {
  padding: 0;
}
.nav .dx-treeview-item {
  padding: 0;

  & .dx-treeview-expander-icon-stub {
    display: none;
  }
  & .dx-treeview-custom-expand-icon {
    display: none;
  }
  & .dx-treeview-custom-collapse-icon {
    display: none;
  }
}
.q-drawer--mini .dx-treeview-item {
  padding: 0 !important;
}
.nav .dx-treeview-item.dx-state-hover,
.nav .dx-state-selected > .dx-treeview-item,
.nav .dx-treeview-item.dx-state-focused {
  background-color: transparent;
}
.external-link {
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.5s ease-in-out;
  // font-size: 1rem !important;
  // width: 1rem;
  &:hover {
    opacity: 1;
  }
}
.expand-chevron {
  transform: rotate(0deg);
  transition: transform 0.2s ease;
  &--open {
    transform: rotate(90deg);
  }
}
.open-in-new {
  opacity: 0.1;
  cursor: pointer;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 1;
  }
}
.nav .menu-item {
  margin-top: 0.15rem;
  margin-bottom: 0.15rem;
  margin-right: 0.5rem;
  padding: 0.65rem;
}
.selected {
  color: $primary !important;
}
.grayed-out {
  color: $grey-9 !important;
}
.nav-header {
  padding-left: 0.5rem;
}
.nav-header__user {
  color: $grey-7;
}
.nav .nav-header__company {
  margin-top: 0.75rem;
}
.nav-header__company .avatar-edit {
  color: $grey-9;
}
.nav-header__actions {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  .q-btn {
    color: $primary;
  }
}
.company-name {
  color: inherit;
}
.company-address {
  color: $grey-7;
}
</style>
